import * as React from 'react';
import { useToast } from '@fragments-sdk/ui';
import {
  buildFindingsIssueBody,
  buildFindingsIssueTitle,
} from '@/lib/github-issue-body';
import {
  formatStatusPatchSummary,
  type SingleStatusPatchResult,
  type StatusPatchSummary,
} from '../findings-operator-model';
import type { AutofixResult, FindingDoc, WaiverDraft } from '../findings-types';
import {
  hasCloudPolicyManagement,
  type PlanId,
} from '../../../../../convex/billing/plans';
import { plural } from '../findings-display-model';

type StatusMutation = (args: any) => Promise<unknown>;
type WaiverMutation = (args: any) => Promise<unknown>;
type LinkIssueMutation = (args: any) => Promise<unknown>;

type RepositoryFindingActionsArgs = {
  selectedProjectId?: string;
  org?: { _id: string } | null;
  orgPlan: PlanId;
  repoFullName?: string;
  readOnly: boolean;
  canCreateAutofix: boolean;
  canCreateWaiver: boolean;
  canCreateGitHubIssue: boolean;
  updateStatus: StatusMutation;
  bulkUpdateStatus: StatusMutation;
  createWaiver: WaiverMutation;
  bulkLinkIssue: LinkIssueMutation;
  copyToClipboard: (text: string) => void;
  onIssueCreated: () => void;
};

export function useRepositoryFindingActions({
  selectedProjectId,
  org,
  orgPlan,
  repoFullName,
  readOnly,
  canCreateAutofix,
  canCreateWaiver,
  canCreateGitHubIssue,
  updateStatus,
  bulkUpdateStatus,
  createWaiver,
  bulkLinkIssue,
  copyToClipboard,
  onIssueCreated,
}: RepositoryFindingActionsArgs) {
  const { success: toastSuccess, error: toastError } = useToast();
  const [fixLoading, setFixLoading] = React.useState(false);
  const [fixResult, setFixResult] = React.useState<AutofixResult | null>(null);
  const [issueLoading, setIssueLoading] = React.useState(false);
  const [waiverDraft, setWaiverDraft] = React.useState<WaiverDraft | null>(
    null,
  );
  const [detailFinding, setDetailFinding] = React.useState<FindingDoc | null>(
    null,
  );

  function handleCopy(text: string) {
    copyToClipboard(text);
    toastSuccess('Copied');
  }

  function reportStatusPatch(result: StatusPatchSummary) {
    const summary = formatStatusPatchSummary(result);
    toastSuccess(summary.title, summary.description);
  }

  async function handleBulkStatus(
    findingIds: string[],
    status: 'open' | 'ignored' | 'resolved',
  ) {
    if (findingIds.length === 0) return null;
    if (readOnly) {
      toastError('Read-only demo', 'Demo findings cannot be changed.');
      return null;
    }
    if (status === 'ignored' && !canCreateWaiver) {
      toastError(
        'Waivers unavailable',
        hasCloudPolicyManagement(orgPlan)
          ? 'Only admins and owners can create waivers.'
          : 'Upgrade to Pro to create cloud waivers.',
      );
      return null;
    }
    try {
      const result = (await bulkUpdateStatus({
        findingIds: findingIds as any,
        status,
      })) as StatusPatchSummary;
      reportStatusPatch(result);
      return result;
    } catch (err) {
      toastError(
        'Status update failed',
        err instanceof Error ? err.message : 'Try again in a moment.',
      );
      return null;
    }
  }

  async function handleFindingStatus(
    findingId: string,
    status: 'open' | 'ignored' | 'resolved',
  ) {
    if (readOnly) {
      toastError('Read-only demo', 'Demo findings cannot be changed.');
      return;
    }
    if (status === 'ignored' && !canCreateWaiver) {
      toastError(
        'Waivers unavailable',
        hasCloudPolicyManagement(orgPlan)
          ? 'Only admins and owners can create waivers.'
          : 'Upgrade to Pro to create cloud waivers.',
      );
      return;
    }
    try {
      const result = (await updateStatus({
        findingId: findingId as any,
        status,
      })) as SingleStatusPatchResult;
      reportStatusPatch({
        requested: 1,
        status,
        changed: result.changed ? 1 : 0,
        skipped: result.changed ? 0 : 1,
        skippedReasons: result.skippedReason
          ? { [result.skippedReason]: 1 }
          : undefined,
      });
    } catch (err) {
      toastError(
        'Status update failed',
        err instanceof Error ? err.message : 'Try again in a moment.',
      );
    }
  }

  async function handleAutofix(ids: string[]) {
    if (!selectedProjectId || !org || ids.length === 0) return;
    if (readOnly) {
      toastError('Read-only demo', 'Demo findings cannot create fix PRs.');
      return;
    }
    if (!canCreateAutofix) {
      toastError(
        'Upgrade required',
        'Autofix PRs require a paid plan with GitHub automation.',
      );
      return;
    }
    setFixLoading(true);
    setFixResult(null);
    try {
      const res = await fetch('/api/govern/autofix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId: org._id,
          projectId: selectedProjectId,
          findingIds: ids,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        prUrl?: string;
        fixedCount?: number;
        filesChanged?: string[];
        prs?: unknown[];
        error?: string;
      };
      if (!res.ok || !data.ok) {
        toastError('Autofix failed', data.error ?? 'Try again in a moment.');
      } else {
        const fixedCount = data.fixedCount ?? ids.length;
        setFixResult({
          prUrl: data.prUrl!,
          fixedCount,
          fileCount: data.filesChanged?.length ?? 0,
          prCount: data.prs?.length ?? 1,
        });
        toastSuccess(
          'Fix PR created',
          `${plural(fixedCount, 'finding')} included.`,
        );
      }
    } catch {
      toastError('Autofix failed', 'Network error');
    } finally {
      setFixLoading(false);
    }
  }

  function clearFixResult() {
    setFixResult(null);
  }

  function openWaiverDialog(finding: FindingDoc) {
    if (readOnly) {
      toastError('Read-only demo', 'Demo findings cannot be ignored.');
      return;
    }
    if (!canCreateWaiver) {
      toastError(
        'Waivers unavailable',
        hasCloudPolicyManagement(orgPlan)
          ? 'Only admins and owners can create waivers.'
          : 'Upgrade to Pro to create cloud waivers.',
      );
      return;
    }
    setWaiverDraft({
      finding,
      scope: 'fingerprint',
      expiresInDays: 0,
      reason: '',
      submitting: false,
      error: null,
    });
  }

  async function submitWaiverDraft() {
    if (!waiverDraft) return;
    setWaiverDraft({ ...waiverDraft, submitting: true, error: null });
    try {
      await createWaiver({
        findingId: waiverDraft.finding._id as any,
        scope: waiverDraft.scope,
        reason: waiverDraft.reason.trim() || undefined,
        expiresInDays:
          waiverDraft.expiresInDays > 0 ? waiverDraft.expiresInDays : undefined,
      });
      setWaiverDraft(null);
    } catch (err) {
      setWaiverDraft({
        ...waiverDraft,
        submitting: false,
        error: err instanceof Error ? err.message : 'Could not create waiver',
      });
    }
  }

  async function handleCreateIssue(findings: FindingDoc[]) {
    if (findings.length === 0) return;
    if (readOnly) {
      toastError(
        'Read-only demo',
        'Demo findings cannot create GitHub issues.',
      );
      return;
    }
    if (!canCreateGitHubIssue) {
      toastError(
        'Upgrade required',
        'GitHub issue creation requires a paid plan.',
      );
      return;
    }
    if (!org || !repoFullName) {
      toastError(
        'Issue creation failed',
        !org ? 'Organization not loaded' : 'No repository connected',
      );
      return;
    }
    setIssueLoading(true);
    try {
      const title = buildFindingsIssueTitle(findings);
      const issueBody = buildFindingsIssueBody(findings);
      const res = await fetch('/api/github/create-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId: org._id,
          repoFullName,
          title,
          body: issueBody,
          labels: ['fragments', 'token-violations'],
        }),
      });

      if (res.ok) {
        const data = (await res.json()) as { issueUrl: string };
        const ids = findings.map((f) => f._id) as any[];
        await bulkLinkIssue({ findingIds: ids, githubIssueUrl: data.issueUrl });
        handleCopy(data.issueUrl);
      } else {
        const errData = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        toastError(
          'Issue creation failed',
          errData.error ?? `GitHub returned ${res.status}.`,
        );
        copyToClipboard(issueBody);
      }
    } catch (err) {
      toastError(
        'Issue creation failed',
        err instanceof Error ? err.message : 'Network error',
      );
      copyToClipboard(buildFindingsIssueBody(findings));
    } finally {
      setIssueLoading(false);
      onIssueCreated();
    }
  }

  return {
    fixLoading,
    fixResult,
    issueLoading,
    waiverDraft,
    detailFinding,
    setWaiverDraft,
    setDetailFinding,
    handleCopy,
    handleBulkStatus,
    handleFindingStatus,
    handleAutofix,
    clearFixResult,
    openWaiverDialog,
    submitWaiverDraft,
    handleCreateIssue,
  };
}

export type RepositoryFindingActions = ReturnType<
  typeof useRepositoryFindingActions
>;
